﻿import React, { useState, useRef, useEffect } from 'react'
import { Text, Heading, Input, Button, LoadingSpinner } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase } from '../lib/supabase'
import { useAuthContext } from '../contexts/AuthContext'
import { 
  PaperAirplaneIcon,
  ArrowPathIcon,
  PlusIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'
import HelpModal from '../components/HelpModal'

interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
  type: 'text' | 'image'
}

interface Chat {
  id: number
  subject: string
  profile_id: string
  tess_root_id?: number
  created_at: string
  updated_at?: string
  deleted?: boolean
}

interface Message {
  id: string
  chat_id: number
  user_message?: string
  ai_message?: string
  created_at: string
}

const PROMPT_SHORTCUTS = [
  {
    label: 'Melhorar mensagem',
    prompt: `Reescreva a mensagem abaixo de forma clara, objetiva e profissional.

Regras:

Simplifique a linguagem
Corrija erros gramaticais
Seja direto e fluido
Evite termos técnicos desnecessários
Nao use travessoes

Retorne apenas o texto reescrito.

Mensagem:
[COLE AQUI]`
  },
  {
    label: 'Resumir',
    prompt: `Resuma o texto abaixo de forma objetiva, em ate 5 topicos ou um paragrafo curto. Destaque apenas o essencial.

Texto:
[COLE AQUI]`
  },
  {
    label: 'Explicar simples',
    prompt: `Explique o conceito ou trecho abaixo como se eu tivesse pouca familiaridade com o assunto. Use linguagem simples, exemplos quando ajudar, e evite jargao sem definicao.

Conteudo:
[COLE AQUI]`
  },
  {
    label: 'E-mail formal',
    prompt: `Redija um e-mail formal em portugues com base no contexto abaixo. Inclua saudacao, corpo objetivo e encerramento cordial. Tom profissional e direto.

Contexto / pedido:
[COLE AQUI]`
  },
  {
    label: 'Gerar plano de acao',
    prompt: `Com base no conteudo abaixo, crie um plano de acao com: objetivo, etapas, prazo sugerido e responsaveis (se aplicavel).

Conteudo:
[COLE AQUI]`
  },
  {
    label: 'Extrair pendencias',
    prompt: `Leia o conteudo abaixo e extraia:
- Pendencias
- Responsaveis (se houver)
- Proximos passos

Conteudo:
[COLE AQUI]`
  },
  {
    label: 'Resposta a cliente',
    prompt: `Redija uma resposta ao cliente com empatia e objetividade, resolvendo a solicitacao de forma clara.

Contexto:
[COLE AQUI]`
  }
]

function countPasiGptExchangedMessages(
  rows: Array<{ user_message?: string | null; ai_message?: string | null }>
): number {
  return rows.reduce((acc, row) => {
    const hasUser = !!row.user_message?.trim()
    const hasAi = !!row.ai_message?.trim()
    return acc + (hasUser ? 1 : 0) + (hasAi ? 1 : 0)
  }, 0)
}

const PASI_GPT_LONG_CONVERSATION_WARN_AFTER = 30
const PASI_GPT_LOCK_CONVERSATION_AFTER = 100

const PasiGPT = () => {
  const { user, profile } = useAuthContext()

  const getFirstName = (): string => {
    if (profile?.name?.trim()) {
      return profile.name.trim().split(/\s+/)[0]
    }
    if (user?.email) {
      return user.email.split('@')[0]
    }
    return 'Usuário'
  }

  /** Remove o prefixo enviado só à API (ex.: "Diego: ") na exibição do chat. */
  const displayUserMessage = (raw: string): string => {
    const prefix = `${getFirstName()}: `
    return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw
  }
  const [chats, setChats] = useState<Chat[]>([])
  const [currentChat, setCurrentChat] = useState<Chat | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingChatId, setEditingChatId] = useState<number | null>(null)
  const [editingChatName, setEditingChatName] = useState('')
  const [creatingChat, setCreatingChat] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [showHelpModal, setShowHelpModal] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Carregar chats do Supabase com retry e timeout
  useEffect(() => {
    const fetchChats = async (attempt: number = 0) => {
      if (!user) return
      
      try {
        setLoading(true)
        setError(null)
        
        // Timeout de 10 segundos
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Timeout: A requisição demorou muito para responder')), 10000)
        })

        const queryPromise = supabase
          .from('pasi_gpt_chats')
          .select('*')
          .eq('profile_id', user.id)
          .eq('deleted', false)
          .order('created_at', { ascending: false })

        const result = await Promise.race([queryPromise, timeoutPromise])

        if (result.error) {
          throw result.error
        }

        setChats(result.data || [])
        if (result.data && result.data.length > 0) {
          setCurrentChat(result.data[0])
        }
        setRetryCount(0)
      } catch (err: any) {
        console.error(`Tentativa ${attempt + 1} falhou:`, err)
        setRetryCount(attempt + 1)
        
        if (attempt < 2) { // 3 tentativas total
          setTimeout(() => {
            fetchChats(attempt + 1)
          }, 1000 * Math.pow(2, attempt)) // Backoff exponencial
        } else {
          setError(err.message || 'Erro ao carregar chats')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchChats()
  }, [user])

  // Função para retry manual
  const retryFetchChats = async () => {
    setError(null)
    setRetryCount(0)
    setLoading(true)
    
    try {
      const { data, error } = await supabase
        .from('pasi_gpt_chats')
        .select('*')
        .eq('profile_id', user?.id)
        .eq('deleted', false)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      setChats(data || [])
      if (data && data.length > 0) {
        setCurrentChat(data[0])
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar chats')
    } finally {
      setLoading(false)
    }
  }

  // Carregar mensagens quando um chat for selecionado
  useEffect(() => {
    const fetchMessages = async () => {
      if (!currentChat) {
        setMessages([])
        return
      }

      try {
        setMessagesLoading(true)
        const { data, error } = await supabase
          .from('pasi_gpt_messages')
          .select('*')
          .eq('chat_id', currentChat.id)
          .order('created_at', { ascending: true })

        if (error) {
          throw error
        }

        setMessages(data || [])
      } catch (err: any) {
        setError(err.message)
      } finally {
        setMessagesLoading(false)
      }
    }

    fetchMessages()
  }, [currentChat])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const createNewChat = async () => {
    if (!user) return
    
    try {
      setCreatingChat(true)
      setError(null)
      
      // Chamar API do n8n
      const n8nResponse = await fetch('https://webhook.pasiai.com/webhook/e0abefa4-785f-48fd-bbc4-8fb8eba4a371', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_id: user.id,
          action: "new"
        })
      })

      if (!n8nResponse.ok) {
        throw new Error('Erro ao criar conversa no n8n')
      }

      const n8nData = await n8nResponse.json()
      
      // Recarregar chats do Supabase para pegar o chat criado pelo n8n
      const { data: chatsData, error: chatsError } = await supabase
        .from('pasi_gpt_chats')
        .select('*')
        .eq('profile_id', user.id)
        .eq('deleted', false)
        .order('created_at', { ascending: false })

      if (chatsError) {
        throw chatsError
      }

      setChats(chatsData || [])
      
      // Selecionar o primeiro chat (mais recente)
      if (chatsData && chatsData.length > 0) {
        setCurrentChat(chatsData[0])
        setInputMessage('')
        setTimeout(() => inputRef.current?.focus(), 100)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setCreatingChat(false)
    }
  }

  const sendMessage = async () => {
    if (!inputMessage.trim() || !currentChat) return
    if (countPasiGptExchangedMessages(messages) > PASI_GPT_LOCK_CONVERSATION_AFTER) return

    const messageText = inputMessage.trim()
    const normalizedMessageText = messageText
      .replace(/\s*\n+\s*/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .replace(/"/g, "'")
      .trim()
    const messageForApi = `${getFirstName()}: ${normalizedMessageText}`
    setInputMessage('')
    setMessagesLoading(true)

    try {
      // Chamar API do n8n (que já salva as mensagens)
      const n8nResponse = await fetch('https://webhook.pasiai.com/webhook/e0abefa4-785f-48fd-bbc4-8fb8eba4a371', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_id: user?.id,
          action: "message",
          chat_id: currentChat.id,
          message: messageForApi
        })
      })

      if (!n8nResponse.ok) {
        throw new Error('Erro ao enviar mensagem para n8n')
      }

      const n8nData = await n8nResponse.json()

      // Recarregar mensagens do Supabase após o n8n processar
      setTimeout(async () => {
        try {
          const { data: messagesData, error: messagesError } = await supabase
            .from('pasi_gpt_messages')
            .select('*')
            .eq('chat_id', currentChat.id)
            .order('created_at', { ascending: true })

          if (messagesError) {
            throw messagesError
          }

          setMessages(messagesData || [])
        } catch (err: any) {
          setError(err.message)
        } finally {
          setMessagesLoading(false)
        }
      }, 1500)
    } catch (err: any) {
      setError(err.message)
      setMessagesLoading(false)
    }
  }

  const generateAIResponse = (userInput: string): string => {
    const responses = [
      "Entendo sua solicitação. Vou analisar os dados e fornecer insights detalhados sobre o que você precisa.",
      "Baseado na sua pergunta, posso ajudar com isso. Deixe-me processar as informações e gerar uma resposta completa.",
      "Excelente pergunta! Vou criar um relatório personalizado com os dados mais relevantes para sua análise.",
      "Posso auxiliar você com essa tarefa. Vou automatizar o processo e gerar os resultados em tempo real.",
      "Perfeito! Vou utilizar os algoritmos de IA para processar essa informação e fornecer insights valiosos.",
      "Com base nos dados disponíveis, posso gerar análises avançadas e identificar padrões importantes.",
      "Vou aplicar técnicas de machine learning para otimizar esse processo e maximizar os resultados.",
      "Entendi sua necessidade. Vou criar uma solução personalizada usando as melhores práticas de IA."
    ]
    return responses[Math.floor(Math.random() * responses.length)]
  }

  const startEditingChat = (chat: Chat) => {
    setEditingChatId(chat.id)
    setEditingChatName(chat.subject)
  }

  const cancelEditingChat = () => {
    setEditingChatId(null)
    setEditingChatName('')
  }

  const saveChatName = async (chatId: number) => {
    if (!editingChatName.trim()) return

    try {
      const { error } = await supabase
        .from('pasi_gpt_chats')
        .update({ subject: editingChatName.trim() })
        .eq('id', chatId)

      if (error) {
        throw error
      }

      // Atualizar lista de chats
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, subject: editingChatName.trim() }
          : chat
      ))

      // Se for o chat atual, atualizar também
      if (currentChat?.id === chatId) {
        setCurrentChat(prev => prev ? { ...prev, subject: editingChatName.trim() } : null)
      }

      setEditingChatId(null)
      setEditingChatName('')
    } catch (err: any) {
      setError(err.message)
    }
  }

  const deleteChat = async (chatId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este chat?')) {
      return
    }

    try {
      // Marcar chat como excluído (soft delete)
      const { error } = await supabase
        .from('pasi_gpt_chats')
        .update({ deleted: true })
        .eq('id', chatId)

      if (error) {
        throw error
      }

      // Remover da lista local
      setChats(prev => prev.filter(chat => chat.id !== chatId))

      // Se o chat excluído era o atual, limpar seleção
      if (currentChat?.id === chatId) {
        setCurrentChat(null)
        setMessages([])
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const applyShortcut = (prompt: string) => {
    setInputMessage(prompt)
    setTimeout(() => {
      if (!inputRef.current) return
      inputRef.current.focus()
      const placeholder = '[COLE AQUI]'
      const start = prompt.indexOf(placeholder)
      if (start >= 0) {
        inputRef.current.setSelectionRange(start, start + placeholder.length)
      }
    }, 0)
  }

  const totalExchangedMessages = countPasiGptExchangedMessages(messages)
  const conversationLockedByLength =
    totalExchangedMessages > PASI_GPT_LOCK_CONVERSATION_AFTER

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" className="text-gray-600 dark:text-gray-400 mb-2">
              Carregando chats...
            </Text>
            {retryCount > 0 && (
              <Text size="sm" className="text-gray-500 dark:text-gray-500">
                Tentativa {retryCount} de 3...
              </Text>
            )}
          </div>
        </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <Text size="lg" className="text-red-600 dark:text-red-400 mb-2">
              Erro ao carregar chats
            </Text>
            <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </Text>
            <Button
              onClick={retryFetchChats}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Tentar Novamente
            </Button>
            {retryCount > 0 && (
              <Text size="xs" className="text-gray-500 dark:text-gray-500 mt-2">
                Já tentamos {retryCount} vezes
              </Text>
            )}
          </div>
        </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
        <PermissionGuard 
          moduleName="pasigpt" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="h-full flex flex-col">
            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden bg-gray-50 dark:bg-gray-900">
              {/* Sidebar de Chats */}
              <div className="w-52 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
                {/* Botão Nova Conversa */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <Button
                    onClick={createNewChat}
                    disabled={creatingChat}
                    className="w-full flex items-center justify-center gap-2"
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    {creatingChat ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Criando chat...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-5 w-5" />
                        Nova Conversa
                      </>
                    )}
                  </Button>
                  {creatingChat && (
                    <Text size="xs" className="text-center text-gray-500 dark:text-gray-400 mt-2">
                      Criando chat, aguarde...
                    </Text>
                  )}
                </div>

                {/* Lista de Chats */}
                <div className="flex-1 overflow-y-auto">
                  {chats.map((chat) => (
                    <div
                      key={chat.id}
                      className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                        currentChat?.id === chat.id ? 'bg-gray-100 dark:bg-gray-700' : ''
                      }`}
                      onClick={() => !editingChatId && setCurrentChat(chat)}
                    >
                      {editingChatId === chat.id ? (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={editingChatName}
                            onChange={(e) => setEditingChatName(e.target.value)}
                            className="flex-1 text-sm"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                saveChatName(chat.id)
                              } else if (e.key === 'Escape') {
                                cancelEditingChat()
                              }
                            }}
                            autoFocus
                          />
                          <button
                            onClick={() => saveChatName(chat.id)}
                            className="p-1 text-green-600 hover:text-green-700 transition-colors"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={cancelEditingChat}
                            className="p-1 text-red-600 hover:text-red-700 transition-colors"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <Text size="sm" className="font-medium text-gray-900 dark:text-white truncate text-left">
                              {chat.subject}
                            </Text>
                            <Text size="xs" className="text-gray-500 dark:text-gray-400 text-left">
                              {new Date(chat.created_at).toLocaleDateString('pt-BR')}
                            </Text>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                startEditingChat(chat)
                              }}
                              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                deleteChat(chat.id)
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Atalhos de prompt */}
                <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
                  <Text size="xs" className="text-gray-500 dark:text-gray-400 mb-2">
                    Atalhos de prompt
                  </Text>
                  <div className="space-y-1.5">
                    {PROMPT_SHORTCUTS.map((shortcut) => (
                      <button
                        key={shortcut.label}
                        onClick={() => applyShortcut(shortcut.prompt)}
                        className="w-full text-left px-3 py-2 text-xs font-medium rounded-lg border border-gray-200/90 dark:border-gray-600/80 bg-white/90 dark:bg-gray-800/80 text-gray-700 dark:text-gray-100 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-white dark:hover:bg-gray-800 shadow-sm transition-all"
                      >
                        {shortcut.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Área de Chat */}
              <div className="flex-1 flex flex-col bg-white dark:bg-gray-900">
                {currentChat ? (
                  <>
                    {/* Mensagens */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 && !messagesLoading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center max-w-md">
                            <div 
                              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: BRAND_COLOR }}
                            >
                              <SparklesIcon className="h-8 w-8 text-white" />
                            </div>
                            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
                              Chat Iniciado
                            </Heading>
                            <Text size="lg" color="muted" className="mb-4">
                              Este chat está pronto! Digite uma mensagem para começar a conversa.
                            </Text>
                            <Text size="sm" color="muted">
                              Chat ID: {currentChat.id}
                            </Text>
                          </div>
                        </div>
                      ) : (
                        <>
                          {messages.map((message) => (
                            <div key={message.id} className="space-y-4">
                              {/* Mensagem do usuário */}
                              {message.user_message && (
                                <div className="flex justify-end">
                                  <div
                                    className="max-w-3xl px-4 py-3 rounded-lg"
                                    style={{ backgroundColor: BRAND_COLOR }}
                                  >
                                    <Text size="sm" className="whitespace-pre-wrap text-right text-white">
                                      {displayUserMessage(message.user_message)}
                                    </Text>
                                    <Text size="xs" className="mt-2 text-white text-right opacity-80">
                                      {new Date(message.created_at).toLocaleDateString('pt-BR')} às {new Date(message.created_at).toLocaleTimeString('pt-BR', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </Text>
                                  </div>
                                </div>
                              )}

                              {/* Mensagem da IA */}
                              {message.ai_message && (
                                <div className="flex justify-start">
                                  <div className="max-w-3xl px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">
                                    <div className="flex items-start gap-3">
                                      <div 
                                        className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-1"
                                        style={{ backgroundColor: BRAND_COLOR }}
                                      >
                                        <SparklesIcon className="h-4 w-4 text-white" />
                                      </div>
                                      <div className="flex-1">
                                        <Text size="sm" className="whitespace-pre-wrap text-left">
                                          {message.ai_message}
                                        </Text>
                                        <Text size="xs" className="mt-2 text-gray-500 dark:text-gray-400 text-left">
                                          {new Date(message.created_at).toLocaleDateString('pt-BR')} às {new Date(message.created_at).toLocaleTimeString('pt-BR', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                          })}
                                        </Text>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Indicador de digitação */}
                          {messagesLoading && (
                            <div className="flex justify-start">
                              <div className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-6 h-6 rounded flex items-center justify-center"
                                    style={{ backgroundColor: BRAND_COLOR }}
                                  >
                                    <SparklesIcon className="h-4 w-4 text-white" />
                                  </div>
                                  <div className="flex space-x-1">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Input de Mensagem */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      {conversationLockedByLength ? (
                        <p className="text-xs text-red-700 dark:text-red-300 mb-3 leading-relaxed border border-red-200 dark:border-red-800/80 rounded-lg px-3 py-2 bg-red-50 dark:bg-red-950/40">
                          Esta conversa está bastante longa (mais de {PASI_GPT_LOCK_CONVERSATION_AFTER} mensagens trocadas). Com o histórico muito extenso, o modelo recebe muito contexto de uma vez, o que pode reduzir a precisão das respostas e aumentar o risco de omissões. Para voltar a ter respostas mais estáveis, use o botão{' '}
                          <span className="font-semibold">+ Nova Conversa</span>
                          {' '}na barra lateral à esquerda e inicie um chat novo com o mesmo assunto, se precisar.
                        </p>
                      ) : (
                        totalExchangedMessages > PASI_GPT_LONG_CONVERSATION_WARN_AFTER && (
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 leading-relaxed">
                            Esta conversa está bastante longa. Para garantir a melhor qualidade de resposta, inicie um novo chat.
                          </p>
                        )
                      )}
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <textarea
                            ref={inputRef}
                            placeholder={
                              conversationLockedByLength
                                ? 'Limite de mensagens atingido — inicie + Nova Conversa'
                                : 'Digite sua mensagem...'
                            }
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                if (!conversationLockedByLength) {
                                  sendMessage()
                                }
                              }
                            }}
                            rows={4}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20 resize-none"
                            disabled={messagesLoading || conversationLockedByLength}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={sendMessage}
                            disabled={
                              !inputMessage.trim() ||
                              messagesLoading ||
                              conversationLockedByLength
                            }
                            className="p-2"
                            style={{ backgroundColor: BRAND_COLOR }}
                          >
                            <PaperAirplaneIcon className="h-5 w-5 text-white" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-2 leading-relaxed">
                        O PasiGPT é uma IA interna que está sendo testada e treinada, por isso pode cometer erros ou apresentar base de conhecimento desatualizada. Confira sempre informações importantes. Em caso de informações inconsistentes ou para enviar sugestões, informe o departamento responsável (
                        <button
                          type="button"
                          onClick={() => setShowHelpModal(true)}
                          className="underline font-medium text-red-700 dark:text-red-300 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 rounded-sm bg-transparent border-0 p-0 cursor-pointer text-left"
                          aria-label="Abrir Sugestões ou Precisa de ajuda"
                        >
                          clique aqui
                        </button>
                        ).
                      </p>
                    </div>
                  </>
                ) : (
                  /* Estado vazio */
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md">
                      <div 
                        className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <SparklesIcon className="h-8 w-8 text-white" />
                      </div>
                      <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-2">
                        Como posso ajudar?
                      </Heading>
                      <Text size="lg" color="muted" className="mb-6">
                        Sou o PasiGPT, seu assistente de inteligência artificial. Posso ajudar com análise de dados, relatórios e muito mais.
                      </Text>
                      <Button
                        onClick={createNewChat}
                        disabled={creatingChat}
                        size="lg"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        {creatingChat ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Criando chat...
                          </>
                        ) : (
                          <>
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Iniciar Conversa
                          </>
                        )}
                      </Button>
                      {creatingChat && (
                        <Text size="sm" className="text-center text-gray-500 dark:text-gray-400 mt-3">
                          Criando chat, aguarde...
                        </Text>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </PermissionGuard>
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </div>
  )
}

export default PasiGPT
